import type { LogDomain } from "@/domains/logging";

import { Bot, Maximize2, Settings, Activity, Zap } from "lucide-react";
import { type FC, useState, useEffect } from "react";

import { Card, Heading, Text, Button, Stack, Container, Badge } from "@/components/ui";
import { debugLog } from "@/domains/logging";

import { ChatWindow } from "./components/ChatWindow";
import { ModelSelector } from "./components/ModelSelector";
import { OllamaFullscreenChat } from "./components/OllamaFullscreenChat";
import { OllamaSettingsModal } from "./components/OllamaSettingsModal";
import { useOllamaChat } from "./hooks/useOllamaChat";

/**
 * Statistics Card Component
 */
interface StatCardProperties {
  color?: "blue" | "gray" | "green" | "yellow";
  icon: React.ElementType;
  label: string;
  loading?: boolean;
  value: number | string;
}

const StatCard: FC<StatCardProperties> = ({
  color = "gray",
  icon: Icon,
  label,
  loading,
  value,
}) => {
  const colorClasses = {
    blue: "text-blue-400 bg-blue-400/10",
    gray: "text-slate-400 bg-slate-400/10",
    green: "text-green-400 bg-green-400/10",
    yellow: "text-yellow-400 bg-yellow-400/10",
  };

  return (
    <Card className="p-4">
      <Stack align="center" direction="row" gap={3}>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <Text className="text-xs text-slate-400 uppercase tracking-wide">{label}</Text>
          {loading ? (
            <div className="h-6 w-20 bg-slate-700 animate-pulse rounded mt-1" />
          ) : (
            <Text className="text-lg font-semibold text-white mt-0.5">{value}</Text>
          )}
        </div>
      </Stack>
    </Card>
  );
};

/**
 * View modes for the Ollama interface
 */
type ViewMode = "dashboard" | "fullscreen";

/**
 * OllamaApp - Enterprise-grade interface for local Ollama LLM instances
 * Features:
 * - Dashboard view with statistics
 * - Fullscreen chat mode
 * - Model management
 * - Health monitoring
 * - Settings configuration
 *
 * @returns JSX element with Ollama dashboard or fullscreen chat interface
 *
 */
export const OllamaApp: FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("dashboard");
  const [showSettings, setShowSettings] = useState(false);
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);

  const { checkHealth, currentModel, getAvailableModels, messages, setModel } = useOllamaChat();

  /**
   * Initialize - Check health and load models
   */
  useEffect(() => {
    const initialize = async () => {
      debugLog("info", "llm" as LogDomain, "OllamaApp: Initializing...");
      setStatsLoading(true);

      try {
        // Check health
        const healthy = await checkHealth();
        setIsHealthy(healthy);

        if (healthy) {
          // Load available models
          const models = await getAvailableModels();
          setAvailableModels(models);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        debugLog("error", "llm" as LogDomain, `OllamaApp: Initialization error: ${errorMessage}`);
        setIsHealthy(false);
      } finally {
        setStatsLoading(false);
      }
    };

    void initialize();
  }, [checkHealth, getAvailableModels]);

  /**
   * Fullscreen mode
   */
  if (viewMode === "fullscreen") {
    return (
      <OllamaFullscreenChat
        onClose={() => {
          setViewMode("dashboard");
        }}
      />
    );
  }

  /**
   * Dashboard mode
   */
  return (
    <Container maxWidth="full" padding="lg">
      <Stack direction="col" gap={6}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Heading
              className="text-2xl font-bold text-white mb-2 flex items-center gap-3"
              level={2}
            >
              <Bot className="w-7 h-7 text-blue-400" />
              Ollama
              {isHealthy !== null && (
                <Badge className="ml-2" variant={isHealthy ? "success" : "destructive"}>
                  {isHealthy ? "Online" : "Offline"}
                </Badge>
              )}
            </Heading>
            <Text className="text-slate-400">
              Lokale KI-Modelle für private und sichere Konversationen
            </Text>
          </div>

          <Stack direction="row" gap={2}>
            <Button
              className="gap-2"
              onClick={() => {
                setShowSettings(true);
              }}
              size="sm"
              variant="outline"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Button>
          </Stack>
        </div>

        {/* Statistics Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {(() => {
            let statusColor: "blue" | "gray" | "green" | "yellow";
            let statusValue: string;

            if (isHealthy === null) {
              statusColor = "gray";
              statusValue = "Checking...";
            } else if (isHealthy) {
              statusColor = "green";
              statusValue = "Running";
            } else {
              statusColor = "yellow";
              statusValue = "Offline";
            }

            return (
              <StatCard
                color={statusColor}
                icon={Activity}
                label="Status"
                loading={statsLoading}
                value={statusValue}
              />
            );
          })()}
          <StatCard
            color="blue"
            icon={Bot}
            label="Current Model"
            loading={statsLoading}
            value={currentModel || "None"}
          />
          <StatCard
            color="yellow"
            icon={Zap}
            label="Available Models"
            loading={statsLoading}
            value={availableModels.length}
          />
          <StatCard
            color="gray"
            icon={Bot}
            label="Messages"
            loading={false}
            value={messages.length}
          />
        </div>

        {/* Main Chat Interface */}
        <Card className="p-0 bg-slate-800 border-slate-700 overflow-hidden">
          {/* Chat Header */}
          <div className="px-6 py-4 border-b border-slate-700 bg-slate-800/50">
            <Stack align="center" direction="row" justify="between">
              <Stack align="center" direction="row" gap={3}>
                <Bot className="w-5 h-5 text-blue-400" />
                <div>
                  <Heading className="text-base font-semibold text-white" level={3}>
                    Ollama Chat
                  </Heading>
                  <Text className="text-xs text-slate-400 mt-0.5">
                    Conversation with {currentModel}
                  </Text>
                </div>
              </Stack>

              <Stack align="center" direction="row" gap={2}>
                <ModelSelector
                  availableModels={availableModels}
                  currentModel={currentModel}
                  isHealthy={isHealthy ?? false}
                  onModelChange={setModel}
                />
                <Button
                  className="gap-2"
                  onClick={() => {
                    setViewMode("fullscreen");
                  }}
                  size="sm"
                  variant="outline"
                >
                  <Maximize2 className="w-4 h-4" />
                  Fullscreen
                </Button>
              </Stack>
            </Stack>
          </div>

          {/* Chat Window - Responsive Height */}
          <div className="h-[calc(100vh-420px)] min-h-[500px]">
            <ChatWindow />
          </div>
        </Card>

        {/* Offline Warning */}
        {!isHealthy && (
          <Card className="p-4 bg-yellow-900/20 border-yellow-700">
            <Stack align="center" direction="row" gap={3}>
              <Activity className="w-5 h-5 text-yellow-400" />
              <div className="flex-1">
                <Text className="font-semibold text-yellow-200">Ollama is offline</Text>
                <Text className="text-sm text-yellow-300/80 mt-1">
                  Please make sure Ollama is running on localhost:11434. Run:{" "}
                  <code className="px-2 py-0.5 bg-slate-900 rounded">ollama serve</code>
                </Text>
              </div>
              <Button
                onClick={() => {
                  void (async () => {
                    setStatsLoading(true);
                    const healthy = await checkHealth();
                    setIsHealthy(healthy);
                    if (healthy) {
                      const models = await getAvailableModels();
                      setAvailableModels(models);
                    }
                    setStatsLoading(false);
                  })();
                }}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            </Stack>
          </Card>
        )}
      </Stack>

      {/* Settings Modal */}
      {showSettings ? (
        <OllamaSettingsModal
          availableModels={availableModels}
          currentModel={currentModel}
          isOpen={showSettings}
          onClose={() => {
            setShowSettings(false);
          }}
          onModelChange={setModel}
        />
      ) : null}
    </Container>
  );
};
